import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
// AdminLayout is handled by the parent AdminAudioPage
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tags, Pencil, Trash2, Check, X, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';

const AdminAudioTagsPage = () => {
  const { language } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState('');
  const [creating, setCreating] = useState(false);
  const [editTag, setEditTag] = useState(null); // { name, count }
  const [editValue, setEditValue] = useState('');
  const [deleteTag, setDeleteTag] = useState(null);
  const [error, setError] = useState('');

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/audio/tags');
      setTags(res.data.tags || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  const handleCreate = async () => {
    const name = newTag.trim().toLowerCase();
    if (!name) return;
    setCreating(true);
    setError('');
    try {
      await axios.post('/api/admin/audio/tags', { name });
      setNewTag('');
      fetchTags();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async () => {
    const name = editValue.trim().toLowerCase();
    if (!name || !editTag) return;
    setError('');
    try {
      await axios.patch(`/api/admin/audio/tags/${encodeURIComponent(editTag.name)}`, { name });
      setEditTag(null);
      fetchTags();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!deleteTag) return;
    try {
      await axios.delete(`/api/admin/audio/tags/${encodeURIComponent(deleteTag.name)}`);
      setDeleteTag(null);
      fetchTags();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold font-cinzel flex items-center gap-2">
            <Tags className="h-6 w-6" />
            {t('admin_audio_tags_title', language)}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {tags.length} {t('admin_audio_tags_count', language)}
          </p>
        </div>

        {/* Créer un tag */}
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-2">{t('admin_audio_add_tag', language)}</p>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder={t('admin_audio_tag_placeholder', language)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
              <Button onClick={handleCreate} disabled={creating || !newTag.trim()}>
                <Plus className="h-4 w-4 mr-1" />
                {t('admin_audio_add_tag', language)}
              </Button>
            </div>
            {error && <p className="text-sm text-destructive mt-2">{error}</p>}
          </CardContent>
        </Card>

        {/* Liste des tags */}
        {loading ? (
          <div className="animate-pulse text-muted-foreground">{t('loading', language)}</div>
        ) : (
          <div className="space-y-2">
            {tags.map((tag) => (
              <Card key={tag.name}>
                <CardContent className="flex items-center gap-3 p-3">
                  {editTag?.name === tag.name ? (
                    <>
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="flex-1 h-8"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditTag(null); }}
                        autoFocus
                      />
                      <Button variant="ghost" size="icon" onClick={handleRename} title={t('save', language)}>
                        <Check className="h-4 w-4 text-green-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setEditTag(null)} title={t('cancel', language)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Badge variant="secondary" className="text-sm px-2 py-0.5">{tag.name}</Badge>
                      <span className="text-xs text-muted-foreground flex-1">
                        {tag.count} track{tag.count > 1 ? 's' : ''}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setEditTag(tag); setEditValue(tag.name); setError(''); }}
                        title={t('admin_audio_rename', language)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTag(tag)}
                        title={t('delete', language)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
            {tags.length === 0 && (
              <p className="text-center text-muted-foreground py-8">{t('admin_audio_no_tracks', language)}</p>
            )}
          </div>
        )}

      {/* Confirm delete */}
      <AlertDialog open={!!deleteTag} onOpenChange={(v) => !v && setDeleteTag(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin_audio_delete_tag_title', language)} "{deleteTag?.name}" ?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin_audio_delete_tag_desc', language)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel', language)}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              {t('delete', language)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminAudioTagsPage;
